/**
 * Axiom Site — motion controller.
 *
 * Ported and consolidated from the Radian theme's motion components:
 *   component-item-reveal, component-heading-reveal, component-heading-highlight,
 *   theme-lenis-scroll, scroll-media-effects, section-sticky-overlap,
 *   component-footer-reveal and component-variant-cursor-parallax.
 *
 * Every feature is driven by data attributes rendered from theme settings and is
 * idempotent, so it can safely re-run after Theme Editor section reloads.
 */
(function () {
  'use strict';

  if (window.AxiomMotion) return;

  var root = document.documentElement;
  var body = document.body;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var desktop = window.matchMedia('(min-width: 750px)');

  function motionOn() {
    return root.classList.contains('js-motion') && !reducedMotion.matches;
  }

  function flag(name) {
    return body.dataset[name] === 'true';
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function within(scope, selector) {
    var nodes = toArray(scope.querySelectorAll ? scope.querySelectorAll(selector) : []);
    if (scope.matches && scope.matches(selector)) nodes.unshift(scope);
    return nodes;
  }

  /* ------------------------------------------------------------------------
     Scroll loop: one rAF-throttled handler shared by every scroll effect.
     ------------------------------------------------------------------------ */
  var scrollTasks = [];
  var frame = null;

  function runScrollTasks() {
    frame = null;
    for (var i = 0; i < scrollTasks.length; i++) scrollTasks[i]();
  }

  function requestScrollFrame() {
    if (frame === null) frame = window.requestAnimationFrame(runScrollTasks);
  }

  window.addEventListener('scroll', requestScrollFrame, { passive: true });
  window.addEventListener('resize', requestScrollFrame);

  /* ------------------------------------------------------------------------
     Reveal
     ------------------------------------------------------------------------ */
  var revealObserver = null;

  function revealGroupIndex(el) {
    var group = el.closest('[data-reveal-group]') || el.parentElement;
    if (!group) return 0;
    var items = toArray(group.querySelectorAll('[data-reveal]')).filter(function (item) {
      return (item.closest('[data-reveal-group]') || item.parentElement) === group;
    });
    var index = items.indexOf(el);
    return index < 0 ? 0 : index;
  }

  function showReveal(el) {
    if (el.classList.contains('is-inview')) return;
    var stagger = parseFloat(getComputedStyle(root).getPropertyValue('--reveal-stagger')) || 0;
    var base = parseFloat(el.dataset.revealDelay || '0');
    var index = el.hasAttribute('data-reveal-index') ? parseInt(el.dataset.revealIndex, 10) : revealGroupIndex(el);
    el.style.setProperty('--reveal-delay', base + Math.min(index, 12) * stagger + 'ms');
    el.classList.add('is-inview');
    el.addEventListener(
      'animationend',
      function done(event) {
        if (event.target !== el) return;
        el.removeEventListener('animationend', done);
        el.classList.add('reveal-done');
      }
    );
  }

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          revealObserver.unobserve(entry.target);
          showReveal(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );
    return revealObserver;
  }

  function initReveal(scope) {
    within(scope, '[data-reveal]').forEach(function (el) {
      if (el.classList.contains('is-inview')) return;
      if (!motionOn() || isAboveFoldInEditor(el)) {
        el.classList.add('is-inview', 'reveal-done');
        return;
      }
      getRevealObserver().observe(el);
    });
  }

  function isAboveFoldInEditor(el) {
    // In the Theme Editor, content above the fold should appear immediately so
    // merchants don't wait for animations while editing.
    return Boolean(window.Shopify && window.Shopify.designMode && el.getBoundingClientRect().top < window.innerHeight);
  }

  /* ------------------------------------------------------------------------
     Heading split (words / lines)
     ------------------------------------------------------------------------ */
  var WORD_STAGGER = 45;
  var headingObserver = null;

  function splitNode(node, words) {
    var parts = node.nodeValue.split(/(\s+)/);
    var fragment = document.createDocumentFragment();
    // Punctuation directly after a previous word (e.g. "<em>last</em>,") joins
    // that word so it can never wrap onto its own line.
    var previous = node.previousSibling;
    if (parts[0] && /^[.,;:!?…)\]’”'"]+$/.test(parts[0]) && previous && previous.classList && previous.classList.contains('split-word')) {
      previous.firstChild.appendChild(document.createTextNode(parts.shift()));
    }
    parts.forEach(function (part) {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        fragment.appendChild(document.createTextNode(part));
        return;
      }
      var word = document.createElement('span');
      var inner = document.createElement('span');
      word.className = 'split-word';
      inner.className = 'split-word__inner';
      inner.textContent = part;
      word.appendChild(inner);
      fragment.appendChild(word);
      words.push(word);
    });
    node.parentNode.replaceChild(fragment, node);
  }

  function splitHeading(heading) {
    if (heading.classList.contains('is-split')) return;
    var words = [];
    var walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        if (node.parentElement.closest('svg, .split-word')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (node) {
      var parent = node.parentElement;
      // Keep highlighted words whole so their decoration stays intact.
      if (parent.matches('em, i') && parent.closest('[data-highlight]')) {
        if (!parent.parentElement.classList.contains('split-word__inner')) {
          var word = document.createElement('span');
          var inner = document.createElement('span');
          word.className = 'split-word';
          inner.className = 'split-word__inner';
          parent.parentNode.insertBefore(word, parent);
          inner.appendChild(parent);
          word.appendChild(inner);
          words.push(word);
        }
        return;
      }
      splitNode(node, words);
    });

    var mode = body.dataset.headingReveal;
    var lineIndex = -1;
    var lastTop = null;
    words.forEach(function (word, index) {
      var delay;
      if (mode === 'lines') {
        var top = word.offsetTop;
        if (lastTop === null || Math.abs(top - lastTop) > 4) {
          lineIndex += 1;
          lastTop = top;
        }
        delay = lineIndex * 120;
      } else {
        delay = index * WORD_STAGGER;
      }
      word.firstChild.style.setProperty('--word-delay', delay + (parseFloat(heading.dataset.splitDelay) || 0) + 'ms');
    });
    heading.classList.add('is-split');
  }

  function finishHeading(heading) {
    var words = heading.querySelectorAll('.split-word__inner');
    var last = words[words.length - 1];
    if (!last) return;
    last.addEventListener('animationend', function () {
      heading.classList.add('split-done');
    }, { once: true });
  }

  function getHeadingObserver() {
    if (headingObserver) return headingObserver;
    headingObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          headingObserver.unobserve(entry.target);
          entry.target.classList.add('is-inview');
          finishHeading(entry.target);
          drawHighlight(entry.target, 450);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0 }
    );
    return headingObserver;
  }

  function initHeadings(scope) {
    within(scope, '[data-split]').forEach(function (heading) {
      if (heading.classList.contains('is-inview')) return;
      if (!motionOn()) {
        heading.classList.add('is-split', 'is-inview', 'split-done');
        return;
      }
      splitHeading(heading);
      if (isAboveFoldInEditor(heading)) {
        heading.classList.add('is-inview', 'split-done');
        drawHighlight(heading, 0);
        return;
      }
      getHeadingObserver().observe(heading);
    });
  }

  /* ------------------------------------------------------------------------
     Highlight draw (headings without split, or other elements)
     ------------------------------------------------------------------------ */
  var highlightObserver = null;

  function drawHighlight(el, delay) {
    var target = el.matches('[data-highlight]') ? el : el.querySelector('[data-highlight]');
    if (!target || target.classList.contains('is-drawn')) return;
    window.setTimeout(function () {
      target.classList.add('is-drawn');
    }, delay || 0);
  }

  function initHighlights(scope) {
    within(scope, '[data-highlight]').forEach(function (el) {
      if (el.hasAttribute('data-split')) return;
      if (!motionOn()) {
        el.classList.add('is-drawn');
        return;
      }
      if (!highlightObserver) {
        highlightObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              highlightObserver.unobserve(entry.target);
              drawHighlight(entry.target, 250);
            });
          },
          { rootMargin: '0px 0px -20% 0px' }
        );
      }
      highlightObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Count up
     ------------------------------------------------------------------------ */
  var countObserver = null;

  function parseCount(text) {
    var match = String(text).trim().match(/^([^0-9-]*)(-?[0-9][0-9,.]*)(.*)$/);
    if (!match) return null;
    var raw = match[2];
    var decimals = (raw.split('.')[1] || '').length;
    var value = Number(raw.replace(/,/g, ''));
    if (!isFinite(value)) return null;
    return { prefix: match[1], suffix: match[3], value: value, decimals: decimals, grouping: raw.indexOf(',') > -1 };
  }

  function formatCount(parts, value) {
    var fixed = value.toFixed(parts.decimals);
    if (parts.grouping) {
      fixed = Number(fixed).toLocaleString(undefined, {
        minimumFractionDigits: parts.decimals,
        maximumFractionDigits: parts.decimals
      });
    }
    return parts.prefix + fixed + parts.suffix;
  }

  function runCount(el) {
    var original = el.dataset.countupOriginal || el.textContent;
    var parts = parseCount(original);
    if (!parts) return;
    el.dataset.countupOriginal = original;
    var duration = parseFloat(el.dataset.countupDuration) || 1800;
    var start = performance.now();
    function ease(t) {
      return 1 - Math.pow(1 - t, 4);
    }
    function tick(now) {
      var progress = Math.min((now - start) / duration, 1);
      el.textContent = formatCount(parts, parts.value * ease(progress));
      if (progress < 1) window.requestAnimationFrame(tick);
      else el.textContent = original;
    }
    el.textContent = formatCount(parts, 0);
    window.requestAnimationFrame(tick);
  }

  function initCountups(scope) {
    within(scope, '[data-countup]').forEach(function (el) {
      if (el.dataset.countupBound === 'true' || !motionOn()) return;
      el.dataset.countupBound = 'true';
      if (!countObserver) {
        countObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              countObserver.unobserve(entry.target);
              runCount(entry.target);
            });
          },
          { rootMargin: '0px 0px -10% 0px' }
        );
      }
      countObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Parallax & scroll-scale (scroll-media-effects)
     ------------------------------------------------------------------------ */
  var parallaxItems = [];

  function initParallax(scope) {
    if (!motionOn() || !flag('parallax')) return;
    within(scope, '[data-parallax-speed], [data-scroll-scale]').forEach(function (el) {
      if (parallaxItems.indexOf(el) > -1) return;
      parallaxItems.push(el);
    });
    requestScrollFrame();
  }

  function updateParallax() {
    if (!parallaxItems.length) return;
    var vh = window.innerHeight;
    parallaxItems = parallaxItems.filter(function (el) {
      return el.isConnected;
    });
    parallaxItems.forEach(function (el) {
      var host = el.parentElement || el;
      var rect = host.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      var transform = '';
      if (el.hasAttribute('data-parallax-speed')) {
        if (el.dataset.parallaxDesktopOnly === 'true' && !desktop.matches) {
          el.style.transform = '';
          return;
        }
        var speed = parseFloat(el.dataset.parallaxSpeed) || 0.12;
        var offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
        transform += 'translate3d(0,' + offset.toFixed(2) + 'px,0) ';
      }
      if (el.hasAttribute('data-scroll-scale')) {
        var from = parseFloat(el.dataset.scrollScale) || 1.15;
        var progress = Math.min(Math.max((vh - rect.top) / (vh * 0.9), 0), 1);
        transform += 'scale(' + (from - (from - 1) * progress).toFixed(4) + ')';
      }
      el.style.transform = transform;
    });
  }

  scrollTasks.push(updateParallax);

  /* ------------------------------------------------------------------------
     Sticky stack (section-sticky-overlap)
     ------------------------------------------------------------------------ */
  var stacks = [];

  function initStacks(scope) {
    if (!motionOn()) return;
    within(scope, '[data-stack]').forEach(function (stack) {
      if (stacks.indexOf(stack) > -1) return;
      stacks.push(stack);
    });
    requestScrollFrame();
  }

  function updateStacks() {
    stacks = stacks.filter(function (stack) {
      return stack.isConnected;
    });
    stacks.forEach(function (stack) {
      var items = toArray(stack.querySelectorAll('[data-stack-item]'));
      items.forEach(function (item, index) {
        var next = items[index + 1];
        if (!next) {
          item.style.setProperty('--stack-progress', 0);
          return;
        }
        var itemRect = item.getBoundingClientRect();
        var nextRect = next.getBoundingClientRect();
        var distance = nextRect.top - itemRect.top;
        var progress = 1 - Math.min(Math.max(distance / Math.max(itemRect.height, 1), 0), 1);
        item.style.setProperty('--stack-progress', progress.toFixed(3));
      });
    });
  }

  scrollTasks.push(updateStacks);

  /* ------------------------------------------------------------------------
     Scroll progress custom property (--scroll-p, 0 → 1)
     - default "enter": 0 when the element's top enters the viewport bottom,
       1 after scrolling `data-scroll-range` viewport heights.
     - "through": 0 when the top passes 60% of the viewport, 1 when the
       bottom does (used for timeline lines).
     ------------------------------------------------------------------------ */
  var progressItems = [];

  function initScrollProgress(scope) {
    if (!motionOn()) return;
    within(scope, '[data-scroll-progress]').forEach(function (el) {
      if (progressItems.indexOf(el) > -1) return;
      progressItems.push(el);
    });
    requestScrollFrame();
  }

  function updateScrollProgress() {
    if (!progressItems.length) return;
    var vh = window.innerHeight;
    progressItems = progressItems.filter(function (el) {
      return el.isConnected;
    });
    progressItems.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      var p;
      if (el.dataset.scrollMode === 'through') {
        p = (vh * 0.6 - rect.top) / Math.max(rect.height, 1);
      } else {
        var range = parseFloat(el.dataset.scrollRange) || 0.8;
        p = (vh - rect.top) / (vh * range);
      }
      p = Math.min(Math.max(p, 0), 1);
      el.style.setProperty('--scroll-p', p.toFixed(4));
    });
  }

  scrollTasks.push(updateScrollProgress);

  /* ------------------------------------------------------------------------
     Magnetic buttons (component-variant-cursor-parallax)
     ------------------------------------------------------------------------ */
  function initMagnetic() {
    if (!motionOn() || !flag('magnetic')) return;
    var active = null;
    document.addEventListener('pointermove', function (event) {
      if (!finePointer.matches) return;
      var target = event.target.closest && event.target.closest('.button, [data-magnetic-target]');
      if (active && active !== target) {
        active.style.transform = '';
        active = null;
      }
      if (!target) return;
      active = target;
      var rect = target.getBoundingClientRect();
      var x = (event.clientX - rect.left - rect.width / 2) * 0.22;
      var y = (event.clientY - rect.top - rect.height / 2) * 0.32;
      target.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)';
    }, { passive: true });
    document.addEventListener('pointerleave', function () {
      if (active) active.style.transform = '';
      active = null;
    });
  }

  /* ------------------------------------------------------------------------
     Contextual cursor (product-grid-cursor)
     ------------------------------------------------------------------------ */
  function initCursor() {
    var cursor = document.querySelector('.cursor');
    if (!cursor || !motionOn()) return;
    var label = cursor.querySelector('.cursor__label');
    var x = -200;
    var y = -200;
    var tx = x;
    var ty = y;
    var running = false;

    function loop() {
      x += (tx - x) * 0.2;
      y += (ty - y) * 0.2;
      cursor.style.setProperty('--cx', x.toFixed(1) + 'px');
      cursor.style.setProperty('--cy', y.toFixed(1) + 'px');
      if (Math.abs(tx - x) > 0.1 || Math.abs(ty - y) > 0.1) {
        window.requestAnimationFrame(loop);
      } else {
        running = false;
      }
    }

    document.addEventListener('pointermove', function (event) {
      if (!finePointer.matches) return;
      tx = event.clientX;
      ty = event.clientY;
      var target = event.target.closest && event.target.closest('[data-cursor-label]');
      if (target) {
        if (label.textContent !== target.dataset.cursorLabel) label.textContent = target.dataset.cursorLabel;
        if (!cursor.classList.contains('is-active')) {
          x = tx;
          y = ty;
        }
        cursor.classList.add('is-active');
      } else {
        cursor.classList.remove('is-active');
      }
      if (!running) {
        running = true;
        window.requestAnimationFrame(loop);
      }
    }, { passive: true });

    document.addEventListener('pointerleave', function () {
      cursor.classList.remove('is-active');
    });
  }

  /* ------------------------------------------------------------------------
     Footer reveal (component-footer-reveal)
     ------------------------------------------------------------------------ */
  function updateFooterReveal() {
    var shell = document.querySelector('.footer-shell');
    if (!shell) return;
    if (!flag('footerReveal') || !desktop.matches || !motionOn()) {
      shell.style.removeProperty('--footer-reveal-offset');
      return;
    }
    // offsetTop ignores transforms, so the offset never feeds back into itself.
    var height = shell.offsetHeight;
    var naturalBottom = shell.offsetTop + height - window.scrollY;
    var remaining = Math.min(Math.max(naturalBottom - window.innerHeight, 0), height);
    shell.style.setProperty('--footer-reveal-offset', (-remaining * 0.5).toFixed(1) + 'px');
  }

  scrollTasks.push(updateFooterReveal);

  /* ------------------------------------------------------------------------
     Page scroll progress
     ------------------------------------------------------------------------ */
  var progressBar = document.querySelector('.scroll-progress');

  function updateProgress() {
    if (!progressBar) return;
    var max = root.scrollHeight - window.innerHeight;
    root.style.setProperty('--scroll-progress', max > 0 ? (window.scrollY / max).toFixed(4) : 0);
  }

  if (progressBar) scrollTasks.push(updateProgress);

  /* ------------------------------------------------------------------------
     Smooth scroll (theme-lenis-scroll)
     ------------------------------------------------------------------------ */
  var lenis = null;

  function initSmoothScroll() {
    if (body.dataset.smoothScroll !== 'true' || !motionOn() || !finePointer.matches) return;
    var src = body.dataset.lenisSrc;
    if (!src) return;
    var script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = function () {
      if (typeof window.Lenis !== 'function') return;
      lenis = new window.Lenis({
        duration: 1.15,
        easing: function (t) {
          return Math.min(1, 1.001 - Math.pow(2, -10 * t));
        },
        smoothWheel: true,
        // Let nested scroll areas (docs sidebar, drawers, results) scroll natively.
        prevent: function (node) {
          return !!(node && node.closest && node.closest('[data-lenis-prevent]'));
        }
      });
      function raf(time) {
        lenis.raf(time);
        window.requestAnimationFrame(raf);
      }
      window.requestAnimationFrame(raf);
      lenis.on('scroll', requestScrollFrame);

      // Pause while overlays lock the page.
      new MutationObserver(function () {
        if (root.classList.contains('is-locked')) lenis.stop();
        else lenis.start();
      }).observe(root, { attributes: true, attributeFilter: ['class'] });

      document.addEventListener('click', function (event) {
        var link = event.target.closest && event.target.closest('a[href^="#"]');
        if (!link) return;
        var id = link.getAttribute('href');
        if (id.length < 2) return;
        var target = document.getElementById(decodeURIComponent(id.slice(1)));
        if (!target) return;
        event.preventDefault();
        var headerHeight = parseFloat(getComputedStyle(root).getPropertyValue('--header-height')) || 0;
        lenis.scrollTo(target, { offset: -headerHeight - 16 });
        history.pushState(null, '', id);
      });
    };
    document.head.appendChild(script);
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function initScope(scope) {
    initHeadings(scope);
    initHighlights(scope);
    initReveal(scope);
    initCountups(scope);
    initParallax(scope);
    initStacks(scope);
    initScrollProgress(scope);
    requestScrollFrame();
  }

  function boot() {
    initScope(document);
    initMagnetic();
    initCursor();
    initSmoothScroll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initScope(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    within(event.target, '[data-reveal]').forEach(function (el) {
      el.classList.add('is-inview', 'reveal-done');
    });
    within(event.target, '[data-split]').forEach(function (el) {
      el.classList.add('is-split', 'is-inview', 'split-done');
    });
    drawHighlight(event.target, 0);
  });

  reducedMotion.addEventListener('change', function () {
    if (reducedMotion.matches) root.classList.remove('js-motion');
  });

  window.AxiomMotion = {
    init: initScope,
    lenis: function () {
      return lenis;
    },
    refresh: requestScrollFrame
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
